<template>
  <div class="operation-log">
    <!-- 日志标题和操作按钮 -->
    <div class="log-header">
      <h3>操作日志</h3>
      <div class="log-actions">
        <el-button
          type="primary"
          link
          @click="switchTab('filter')"
        >
          <el-icon><Filter /></el-icon>
          筛选
        </el-button>
        <el-button
          type="primary"
          link
          @click="exportLog"
        >
          <el-icon><Download /></el-icon>
          导出
        </el-button>
      </div>
    </div>

    <!-- 筛选区域 -->
    <el-card v-if="showFilter" class="filter-card" shadow="never">
      <el-form :inline="true" :model="filterParams" class="filter-form">
        <el-form-item label="操作类型">
          <el-select
            v-model="filterParams.operationType"
            placeholder="全部"
            clearable
            style="width: 150px"
          >
            <el-option label="设备绑定" value="DEVICE_BIND" />
            <el-option label="设备解绑" value="DEVICE_UNBIND" />
            <el-option label="设备转移" value="DEVICE_TRANSFER" />
          </el-select>
        </el-form-item>
        <el-form-item label="操作人">
          <el-input
            v-model="filterParams.operatorName"
            placeholder="请输入操作人"
            clearable
            style="width: 150px"
          />
        </el-form-item>
        <el-form-item label="操作时间">
          <el-date-picker
            v-model="filterParams.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="YYYY-MM-DD"
            style="width: 250px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleFilter">查询</el-button>
          <el-button @click="resetFilter">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 日志列表 -->
    <el-table
      :data="logList"
      border
      v-loading="loading"
      style="width: 100%"
      max-height="400"
    >
      <el-table-column prop="createTime" label="操作时间" width="170">
        <template #default="{ row }">
          {{ formatTime(row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column prop="operationType" label="操作类型" width="120">
        <template #default="{ row }">
          <el-tag :type="getOperationTypeTag(row.operationType)" size="small">
            {{ getOperationTypeName(row.operationType) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="operationDesc" label="操作内容" min-width="250">
        <template #default="{ row }">
          {{ row.operationDesc }}
        </template>
      </el-table-column>
      <el-table-column prop="operatorName" label="操作人" width="120" />
      <el-table-column prop="operatorRole" label="角色" width="100">
        <template #default="{ row }">
          <el-tag :type="getRoleTag(row.operatorRole)" size="small">
            {{ getRoleName(row.operatorRole) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="result" label="结果" width="80" align="center">
        <template #default="{ row }">
          <el-icon v-if="row.result === 1" color="#67C23A" size="18">
            <CircleCheckFilled />
          </el-icon>
          <el-icon v-else color="#F56C6C" size="18">
            <CircleCloseFilled />
          </el-icon>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination">
      <el-pagination
        v-model:current-page="pageParams.current"
        v-model:page-size="pageParams.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="pageParams.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { Filter, Download, CircleCheckFilled, CircleCloseFilled } from '@element-plus/icons-vue'
import { getOperationLogList, exportOperationLog } from '@/api/device'

export default {
  name: 'OperationLog',
  components: {
    Filter,
    Download,
    CircleCheckFilled,
    CircleCloseFilled
  },
  setup(props, { emit }) {
    const loading = ref(false)
    const showFilter = ref(false)
    const logList = ref([])
    const filterParams = reactive({
      operationType: '',
      operatorName: '',
      dateRange: []
    })
    const pageParams = reactive({
      current: 1,
      pageSize: 20,
      total: 0
    })

    // 获取日志列表
    const fetchLogList = async () => {
      loading.value = true
      try {
        const params = {
          current: pageParams.current,
          pageSize: pageParams.pageSize,
          operationType: filterParams.operationType || undefined,
          operatorName: filterParams.operatorName || undefined,
          startTime: filterParams.dateRange?.[0] || undefined,
          endTime: filterParams.dateRange?.[1] || undefined
        }

        const response = await getOperationLogList(params)
        if (response.code === 200) {
          logList.value = response.data.records || []
          pageParams.total = response.data.total || 0
        }
      } catch (error) {
        console.error('获取操作日志失败：', error)
      } finally {
        loading.value = false
      }
    }

    // 切换筛选显示
    const switchTab = (tab) => {
      if (tab === 'filter') {
        showFilter.value = !showFilter.value
      }
    }

    // 筛选查询
    const handleFilter = () => {
      pageParams.current = 1
      fetchLogList()
    }

    // 重置筛选
    const resetFilter = () => {
      filterParams.operationType = ''
      filterParams.operatorName = ''
      filterParams.dateRange = []
      handleFilter()
    }

    // 分页
    const handleSizeChange = (size) => {
      pageParams.pageSize = size
      fetchLogList()
    }

    const handleCurrentChange = (page) => {
      pageParams.current = page
      fetchLogList()
    }

    // 导出日志
    const exportLog = async () => {
      try {
        const params = {
          operationType: filterParams.operationType || undefined,
          operatorName: filterParams.operatorName || undefined,
          startTime: filterParams.dateRange?.[0] || undefined,
          endTime: filterParams.dateRange?.[1] || undefined
        }

        const response = await exportOperationLog(params)
        if (response.code === 200) {
          // 下载文件
          const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = `操作日志_${new Date().toLocaleDateString()}.xlsx`
          link.click()
          ElMessage.success('导出成功')
        } else {
          ElMessage.error(response.message || '导出失败')
        }
      } catch (error) {
        console.error('导出失败：', error)
        ElMessage.error('导出失败')
      }
    }

    // 格式化时间
    const formatTime = (time) => {
      if (!time) return '-'
      return time
    }

    // 获取操作类型名称
    const getOperationTypeName = (type) => {
      const typeMap = {
        'DEVICE_BIND': '设备绑定',
        'DEVICE_UNBIND': '设备解绑',
        'DEVICE_TRANSFER': '设备转移'
      }
      return typeMap[type] || type
    }

    // 获取操作类型标签
    const getOperationTypeTag = (type) => {
      const tagMap = {
        'DEVICE_BIND': 'success',
        'DEVICE_UNBIND': 'danger',
        'DEVICE_TRANSFER': 'warning'
      }
      return tagMap[type] || 'info'
    }

    // 获取角色名称
    const getRoleName = (role) => {
      const roleMap = {
        'SUPER_ADMIN': '超级管理员',
        'ADMIN': '管理员',
        'INSPECTOR': '检测员'
      }
      return roleMap[role] || role
    }

    // 获取角色标签
    const getRoleTag = (role) => {
      const tagMap = {
        'SUPER_ADMIN': 'danger',
        'ADMIN': 'warning',
        'INSPECTOR': 'info'
      }
      return tagMap[role] || 'info'
    }

    // 切换到日志标签（供父组件调用）
    const switchToHistory = () => {
      nextTick(() => {
        // 可以在这里执行一些初始化操作
      })
    }

    // 初始化
    onMounted(() => {
      fetchLogList()
    })

    return {
      loading,
      showFilter,
      logList,
      filterParams,
      pageParams,
      fetchLogList,
      switchTab,
      handleFilter,
      resetFilter,
      handleSizeChange,
      handleCurrentChange,
      exportLog,
      formatTime,
      getOperationTypeName,
      getOperationTypeTag,
      getRoleName,
      getRoleTag,
      switchToHistory
    }
  }
}
</script>

<style lang="scss" scoped>
.operation-log {
  .log-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #303133;
    }

    .log-actions {
      display: flex;
      gap: 10px;
    }
  }

  .filter-card {
    margin-bottom: 15px;

    .filter-form {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }
  }

  .pagination {
    margin-top: 15px;
    display: flex;
    justify-content: flex-end;
  }
}
</style>
